/**
 * PotatoPiratesSimulator — sandbox multi-player battle board.
 *
 * Supports 2–6 players. Every fleet is editable; action cards have a target
 * selector listing all ships from all other fleets so players can choose exactly
 * who to attack. Resolution uses runMultiPlayerBattle (fleets execute in order).
 */
import React from 'react'
import { Play, RotateCcw, Trophy } from 'lucide-react'
import {
  cloneState,
  runMultiPlayerBattle,
  type Card,
  type Fleet,
  type GameState,
  type SimEvent,
  type CardType,
} from './engine'
import { CardPalette } from './CardPalette'
import { ShipBoard } from './ShipBoard'
import type { TargetOption } from './PirateCard'

/** Six visually distinct player colours. */
const FLEET_ACCENTS = ['#0ea5e9', '#ef4444', '#16a34a', '#f59e0b', '#8b5cf6', '#ec4899']

interface SandboxSimulatorProps {
  initialState: GameState
  allowedCards?: CardType[]
  disabled?: boolean
  stepMs?: number
}

export function PotatoPiratesSimulator({
  initialState,
  allowedCards,
  disabled = false,
  stepMs = 750,
}: SandboxSimulatorProps) {
  const [editState, setEditState] = React.useState<GameState>(() => cloneState(initialState))
  const [events, setEvents] = React.useState<SimEvent[]>([])
  const [step, setStep] = React.useState(-1)
  const [running, setRunning] = React.useState(false)
  const [result, setResult] = React.useState<ReturnType<typeof runMultiPlayerBattle> | null>(null)
  const timerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)

  const locked = disabled || running || result !== null

  // Animated replay driver.
  React.useEffect(() => {
    if (!running) return
    if (step >= events.length - 1) { setRunning(false); return }
    timerRef.current = setTimeout(() => setStep((s) => s + 1), stepMs)
    return () => { if (timerRef.current) clearTimeout(timerRef.current) }
  }, [running, step, events.length, stepMs])

  const handleRun = () => {
    const res = runMultiPlayerBattle(editState)
    setResult(res)
    if (res.events.length === 0) { setStep(-1); setRunning(false); return }
    setStep(0)
    setRunning(true)
    setEvents(res.events)
  }

  const handleReset = () => {
    if (timerRef.current) clearTimeout(timerRef.current)
    setEvents([])
    setStep(-1)
    setRunning(false)
    setResult(null)
  }

  const updateShipProgram = (fleetId: string, shipId: string, program: Card[]) => {
    setEditState((prev) => ({
      fleets: prev.fleets.map((f) =>
        f.id !== fleetId ? f : { ...f, ships: f.ships.map((s) => (s.id === shipId ? { ...s, program } : s)) },
      ),
    }))
  }

  // Animated potato counts.
  const currentEvent = step >= 0 ? events[step] : null
  const displaySnapshot = currentEvent?.snapshot ?? null
  const potatoesOf = (shipId: string, fallback: number) =>
    displaySnapshot && shipId in displaySnapshot ? displaySnapshot[shipId] : fallback

  const highlightShipId = currentEvent?.targetShipId ?? null
  const sourceShipId = currentEvent?.sourceShipId ?? null
  const highlightCardId = currentEvent?.sourceCardId ?? null

  /**
   * Targets for fleet i = all ships from every other fleet.
   * Labelled as "PlayerName · ShipName" so the selector is unambiguous with 3+ players.
   */
  const targetsFor = (fleetId: string): TargetOption[] =>
    editState.fleets
      .filter((f) => f.id !== fleetId)
      .flatMap((f) =>
        f.ships.map((s) => ({
          id: s.id,
          name: `${f.name} · ${s.name.replace(`${f.name} `, '')}`,
          potatoes: potatoesOf(s.id, s.potatoes),
        })),
      )

  const renderFleet = (fleet: Fleet, idx: number) => {
    const accent = FLEET_ACCENTS[idx % FLEET_ACCENTS.length]
    const editable = !locked
    return (
      <div key={fleet.id} style={{ minWidth: 260, flex: '1 1 260px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          <span style={{ width: 12, height: 12, borderRadius: '50%', background: accent, flexShrink: 0 }} />
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 800, color: 'var(--color-text)' }}>{fleet.name}</h3>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {fleet.ships.map((ship) => {
            const animatedShip = { ...ship, potatoes: potatoesOf(ship.id, ship.potatoes) }
            return (
              <ShipBoard
                key={ship.id}
                ship={animatedShip}
                editable={editable}
                accent={accent}
                targets={targetsFor(fleet.id)}
                onProgramChange={(program) => updateShipProgram(fleet.id, ship.id, program)}
                highlightShipId={highlightShipId === ship.id || sourceShipId === ship.id ? ship.id : null}
                highlightCardId={sourceShipId === ship.id ? highlightCardId : null}
              />
            )
          })}
        </div>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        {/* Palette */}
        <div style={{
          width: 220, flexShrink: 0, position: 'sticky', top: 8,
          background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', borderRadius: 14, padding: 14,
        }}>
          <CardPalette allowedCards={allowedCards} />
        </div>

        {/* Fleet grid */}
        <div style={{ flex: 1, minWidth: 0, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          {editState.fleets.map((fleet, idx) => renderFleet(fleet, idx))}
        </div>
      </div>

      {/* Controls */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <button
          onClick={handleRun} disabled={locked}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 18px',
            borderRadius: 10, border: 'none', fontWeight: 800, fontSize: 14,
            cursor: locked ? 'not-allowed' : 'pointer', opacity: locked ? 0.5 : 1,
            background: 'var(--gradient-primary)', color: 'var(--color-on-primary)',
          }}
        >
          <Play size={16} /> {running ? 'Running…' : 'Run Battle'}
        </button>
        {(result || running) && (
          <button
            onClick={handleReset} disabled={running}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 16px',
              borderRadius: 10, border: '1px solid var(--color-border)', fontWeight: 700, fontSize: 14,
              cursor: running ? 'not-allowed' : 'pointer', opacity: running ? 0.5 : 1,
              background: 'var(--color-surface)', color: 'var(--color-text)',
            }}
          >
            <RotateCcw size={16} /> Reset
          </button>
        )}
      </div>

      {/* Live event ticker */}
      {currentEvent && (
        <div style={{
          padding: '10px 14px', borderRadius: 10, background: 'var(--pp-ticker-bg)', color: 'var(--pp-ticker-text)',
          fontSize: 13, fontFamily: 'ui-monospace, monospace',
        }}>
          {currentEvent.message}
        </div>
      )}

      {/* Result banner */}
      {result && !running && <SandboxResultBanner result={result} fleets={editState.fleets} />}
    </div>
  )
}

function SandboxResultBanner({
  result,
  fleets,
}: {
  result: ReturnType<typeof runMultiPlayerBattle>
  fleets: Fleet[]
}) {
  const { winnerId, winnerName, potatoesDestroyed } = result
  const winnerIdx = fleets.findIndex((f) => f.id === winnerId)
  const accent = FLEET_ACCENTS[winnerIdx >= 0 ? winnerIdx % FLEET_ACCENTS.length : 0]

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px',
      borderRadius: 12, background: 'var(--color-success-subtle)', color: 'var(--color-success)', fontWeight: 800, fontSize: 15,
      border: `2px solid ${accent}`,
    }}>
      <Trophy size={20} style={{ color: accent }} />
      <span>{winnerName ? `${winnerName} wins! 🏆` : "It's a draw!"}</span>
      <span style={{ marginLeft: 'auto', fontWeight: 600, fontSize: 13 }}>
        {potatoesDestroyed} potato{potatoesDestroyed === 1 ? '' : 'es'} destroyed
      </span>
    </div>
  )
}
